import React, { useState, useEffect } from "react";
import { Track, RoomEvent } from "livekit-client";
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  ParticipantName,
  useTracks,
  useIsSpeaking,
  useRoomContext,
} from "@livekit/components-react";
import EgressHelper from "@livekit/egress-sdk";

import "./recordingTemplate.css";

/**
 * Sends START_RECORDING / END_RECORDING signals to the LiveKit egress service.
 * Must be rendered inside LiveKitRoom. Start is sent when the room is connected
 * and at least one track is available (or on first TrackSubscribed).
 */
function EgressSignals() {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;

    EgressHelper.setRoom(room);

    const hasTrack = () => {
      for (const p of Array.from(room.remoteParticipants.values())) {
        if (p.trackPublications.size > 0) return true;
      }
      return false;
    };

    if (hasTrack()) {
      EgressHelper.startRecording();
    } else {
      const handleTrackSubscribed = () => EgressHelper.startRecording();
      room.once(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      return () => room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    }
  }, [room]);

  return null;
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function Timer() {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="recording-timer">
      <span className="recording-timer-label">Recording</span>
      <span className="recording-timer-value">{formatTime(seconds)}</span>
    </div>
  );
}

function Tile() {
  const isSpeaking = useIsSpeaking();

  return (
    <div className={`recording-tile ${isSpeaking ? "recording-tile--speaking" : ""}`}>
      <ParticipantTile />
      <ParticipantName className="recording-tile-name" />
    </div>
  );
}

function RecordingRoom() {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );

  return (
    <div className="recording-container">
      <header className="recording-header">
        <div className="recording-header-left">
          <img src="/logo.png" alt="Logo" className="recording-logo" />
          <span className="recording-title">MYANATOMY INTERVIEW</span>
        </div>
        <Timer />
      </header>

      <div className="recording-video-area">
        <GridLayout tracks={tracks} className="recording-grid">
          <Tile />
        </GridLayout>
      </div>

      <footer className="recording-footer">
        <span className="recording-footer-domain">video.myanatomy.in</span>
      </footer>
    </div>
  );
}

export default function DefaultTemplate() {
  const params = new URLSearchParams(window.location.search);
  const url = params.get("url");
  const token = params.get("token");

  if (!url || !token) {
    return (
      <div className="recording-container recording-container--error">
        <p>Missing url or token query parameters.</p>
      </div>
    );
  }

  return (
    <LiveKitRoom
      serverUrl={url}
      token={token}
      connect={true}
      video
      audio
      connectOptions={{ autoSubscribe: true }}
      options={{ adaptiveStream: true, dynacast: true }}
    >
      <EgressSignals />
      <RecordingRoom />
    </LiveKitRoom>
  );
}
